/**
 * 云海(方案 C2 · v3 噪声重构)。
 *
 * v2(椭圆堆)的问题:无论怎么修金边/合并 fill,形态语言仍是"一坨坨椭圆"——
 * 云的可信度来自**分形边缘**,任何有限个椭圆并集都给不出。v3 改为:
 *
 *  1. 启动时烘焙两张可水平平铺的 fBm 值噪声云带纹理(512×160,一次性 canvas,
 *     零外部资产)。密度场 = fbm + 垂直偏置 → 顶缘破碎、中部致密、底缘微透。
 *  2. 同时烘焙"受光边"纹理:密度场沿 -y 的梯度,只在云顶迎光面有值——
 *     金边从此是真正贴着分形轮廓的光,而非手描的弧线。
 *  3. 前/后两层 TilingSprite 视差滚动(风耦合),每帧按 SkyState 着色:
 *     云体 tint=cloudTint,受光边 tint=cloudEdge + add 混合,昼夜自动过渡。
 */
import { Texture, TilingSprite } from 'pixi.js';
import { Stage, LW, GROUND_Y } from '../engine/Stage';
import { sky } from '../engine/SkyRenderer';
import { world as windSrc } from '../engine/Ticker';

const TEX_W = 512, TEX_H = 160;

/** 哈希 → [0,1):经典 sin 散列,种子区分两层纹理 */
function h2(ix: number, iy: number, seed: number): number {
  const s = Math.sin(ix * 127.1 + iy * 311.7 + seed * 74.7) * 43758.5453;
  return s - Math.floor(s);
}

/** 可平铺值噪声:x 向按晶格周期取模,保证 TilingSprite 无缝 */
function noiseTile(u: number, v: number, fx: number, fy: number, seed: number): number {
  const x = u * fx, y = v * fy;
  const xi = Math.floor(x), yi = Math.floor(y);
  const xf = x - xi, yf = y - yi;
  const sx = xf * xf * (3 - 2 * xf), sy = yf * yf * (3 - 2 * yf);
  const r = (ix: number, iy: number) => h2(((ix % fx) + fx) % fx, iy, seed);
  const a = r(xi, yi), b = r(xi + 1, yi), c = r(xi, yi + 1), d = r(xi + 1, yi + 1);
  return a + (b - a) * sx + (c - a) * sy + (a - b - c + d) * sx * sy;
}

function fbm(u: number, v: number, seed: number): number {
  return (
    noiseTile(u, v, 5, 2, seed) * 0.5 +
    noiseTile(u, v, 10, 4, seed + 7) * 0.27 +
    noiseTile(u, v, 20, 8, seed + 13) * 0.15 +
    noiseTile(u, v, 40, 16, seed + 29) * 0.08
  );
}

const smooth = (a: number, b: number, x: number) => {
  const t = Math.max(0, Math.min(1, (x - a) / (b - a)));
  return t * t * (3 - 2 * t);
};

/** 烘焙一对纹理:[云体, 受光边]。密度场共享,保证边严格贴合形体。 */
function bakeCloud(seed: number): [Texture, Texture] {
  const dens = new Float32Array(TEX_W * TEX_H);
  for (let y = 0; y < TEX_H; y++) {
    const v = y / TEX_H;
    for (let x = 0; x < TEX_W; x++) {
      const u = x / TEX_W;
      const n = fbm(u, v, seed);
      // 垂直偏置:v 越大(越靠下)越致密;顶缘要靠高噪声才成云 → 分形破碎边
      const d = smooth(0.52, 0.78, n + (v - 0.42) * 1.05);
      // 底缘必须衰减到**零**:任何残余 alpha 都会在带底渲出一条横贯硬线,
      // 整带立刻读成"矩形雾板"(v3 首验的主要破绽)
      dens[y * TEX_W + x] = d * (1 - smooth(0.72, 0.96, v));
    }
  }
  const mk = (fill: (i: number, x: number, y: number) => number) => {
    const c = document.createElement('canvas');
    c.width = TEX_W; c.height = TEX_H;
    const ctx = c.getContext('2d')!;
    const img = ctx.createImageData(TEX_W, TEX_H);
    for (let y = 0; y < TEX_H; y++) for (let x = 0; x < TEX_W; x++) {
      const i = y * TEX_W + x;
      const a = fill(i, x, y);
      const o = i * 4;
      img.data[o] = img.data[o + 1] = img.data[o + 2] = 255;
      img.data[o + 3] = Math.max(0, Math.min(255, a * 255)) | 0;
    }
    ctx.putImageData(img, 0, 0);
    return Texture.from(c);
  };
  const body = mk((i) => dens[i]);
  // 受光边 = 密度沿 -y 的正梯度(上方比下方稀 ⇒ 此处是顶面迎光),再被自身密度调制
  const edge = mk((i, x, y) => {
    const up = y >= 2 ? dens[(y - 2) * TEX_W + x] : 0;
    const dn = y < TEX_H - 2 ? dens[(y + 2) * TEX_W + x] : dens[i];
    return Math.max(0, dn - up) * 2.2 * Math.min(1, dens[i] * 3);
  });
  return [body, edge];
}

class Layer {
  body: TilingSprite;
  edge: TilingSprite;
  constructor(
    stage: Stage, layerName: 'cloudSeaBack' | 'cloudSeaFront', seed: number,
    y: number, private scaleY: number, private speed: number, private alpha: number,
  ) {
    const [bt, et] = bakeCloud(seed);
    // 宽度铺到 1.6×LW 并左移 0.3×LW:世界容器按 min(w/LW, h/LH) 缩放居中,
    // 宽屏下画布比逻辑区宽,云海若只铺逻辑宽,两侧会裸出竖直截断线(v3 首验实锤)
    const W = LW * 1.6;
    this.body = new TilingSprite({ texture: bt, width: W, height: TEX_H * scaleY });
    this.edge = new TilingSprite({ texture: et, width: W, height: TEX_H * scaleY });
    for (const s of [this.body, this.edge]) {
      s.position.set(-LW * 0.3, y);
      s.tileScale.set(scaleY * 2.2, scaleY);   // x 向不按整宽拉伸,保持云团比例并让平铺自然重复
    }
    this.edge.blendMode = 'add';
    stage.layers[layerName].addChild(this.body, this.edge);
  }
  update(dt: number, t: number, ph: number) {
    // 漂移(风耦合)+ 极缓垂直呼吸
    const drift = this.speed * (1 + Math.abs(windSrc.wind.x) / 120) * (windSrc.wind.x < 0 ? -1 : 1);
    this.body.tilePosition.x -= drift * dt;
    this.edge.tilePosition.x = this.body.tilePosition.x;
    const bob = Math.sin(t * 0.08 + ph) * 2;
    this.body.y = this.baseY + bob;
    this.edge.y = this.body.y - 1;
    // 着色:全部派生自 SkyState
    const amb = sky.state.ambient;
    this.body.tint = sky.cloudTint;
    this.body.alpha = this.alpha + amb * 0.06;
    this.edge.tint = sky.cloudEdge;
    this.edge.alpha = 0.22 + amb * 0.2;
  }
  private baseY = 0;
  setBaseY(y: number) { this.baseY = y; this.body.y = y; this.edge.y = y; }
}

export class CloudSea {
  private back: Layer;
  private front: Layer;
  private t = 0;
  constructor(stage: Stage) {
    // §C2:front 层永远遮住主岛底部——倒锥自 GROUND_Y 延伸约 +330,
    // front 云带顶缘(纹理上 1/3 处开始有云)需压在 GROUND_Y+60 一带。
    this.back = new Layer(stage, 'cloudSeaBack', 3, GROUND_Y - 26, 0.9, 3.2, 0.36);
    this.front = new Layer(stage, 'cloudSeaFront', 11, GROUND_Y + 34, 1.7, 7.5, 0.52);
    this.back.setBaseY(GROUND_Y - 26);
    this.front.setBaseY(GROUND_Y + 34);
  }
  update(dt: number) {
    this.t += dt;
    this.back.update(dt, this.t, 0);
    this.front.update(dt, this.t, 2.4);
  }
}
